import { Component, Input, Output, EventEmitter, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { OprClient } from '../../../interfaces/anagraphic.interfaces';
import { ShipmentManagementService } from '../../../services/shipment-management.service';

@Component({
  selector: 'app-client-details-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './client-details-popup.component.html',
  styleUrl: './client-details-popup.component.css'
})
export class ClientDetailsPopupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Input() clientCode: string | null = null;
  @Output() closed = new EventEmitter<void>();

  client: OprClient | null = null;
  isLoading = false;
  hasError = false;

  private subscription: Subscription | null = null;
  private readonly keyHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  constructor(private shipmentManagementService: ShipmentManagementService) {}

  ngOnInit(): void {
    document.addEventListener('keydown', this.keyHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] || changes['clientCode']) {
      if (this.visible && this.clientCode) {
        this.loadClient(this.clientCode);
      } else if (!this.visible) {
        this.subscription?.unsubscribe();
        this.client = null;
        this.isLoading = false;
        this.hasError = false;
      }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.keyHandler);
    this.subscription?.unsubscribe();
  }

  private loadClient(clientCode: string): void {
    this.subscription?.unsubscribe();
    this.client = null;
    this.hasError = false;
    this.isLoading = true;

    this.subscription = this.shipmentManagementService.getClientDetails(clientCode).subscribe({
      next: client => {
        this.client = client;
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      }
    });
  }

  close(): void {
    this.closed.emit();
  }

  getStatusBadgeClass(): string {
    switch (this.client?.clientStatus) {
      case 'ACTIVE':
        return 'status-executed';
      case 'PENDING':
        return 'status-pending';
      case 'SUSPENDED':
      case 'TERMINATED':
      case 'DELETED':
        return 'status-failed';
      default:
        return 'status-unknown';
    }
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(err => console.error('Failed to copy text: ', err));
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    }
  }

  hasLegalAddress(): boolean {
    return !!(this.client?.clientLegalAddressStreet || this.client?.clientLegalAddressCity);
  }

  hasOperatingAddress(): boolean {
    return !!(this.client?.clientOperatingAddressStreet || this.client?.clientOperatingAddressCity);
  }

  hasCoordinates(): boolean {
    return this.client?.clientLatitude != null && this.client?.clientLongitude != null;
  }

  getCoordinatesMapUrl(): string {
    return this.hasCoordinates()
      ? `https://www.google.com/maps?q=${this.client!.clientLatitude},${this.client!.clientLongitude}&z=15`
      : '';
  }

  isSignatureRequired(): boolean {
    return this.client?.clientRequestSignature === 'Y';
  }
}
